import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@mui/material";
import { db, storage } from "../../firebase/firebase";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/*
  🔹 Admin LetterHeadUpload Screen
  ------------------------------
  - Only for Admin users.
  - Shows all companies.
  - Allows uploading or updating:
      • Company Letterhead (JPG/PNG)
      • Reference Short Code (e.g. "CLL", "LLL")
  - MD Signature & Stamp are handled separately by MD portal.
*/

export const LetterHeadUpload = () => {
  const [companies, setCompanies] = useState([]);
  const [savingCompanyId, setSavingCompanyId] = useState(null);
  const [formState, setFormState] = useState({});

  // 🔹 Load companies live from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setCompanies(list);
      setFormState((prev) => {
        const next = { ...prev };
        for (const c of list) {
          if (!next[c.id]) {
            next[c.id] = {
              shortCode: c.shortCode || "",
              letterheadUrl: c.letterheadUrl || "",
              letterheadFile: null,
            };
          }
        }
        return next;
      });
    });
    return () => unsub();
  }, []);

  // 🔹 Upload a file to Firebase Storage
  const uploadLetterhead = async (file, companyId) => {
    if (!file) return null;
    const fileName = `${companyId}_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `companyAssets/letterheads/${fileName}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  // 🔹 Save changes for one company
  const handleSaveCompany = async (company) => {
    try {
      setSavingCompanyId(company.id);
      const state = formState[company.id];
      if (!state) return;

      const companyRef = doc(db, "companies", company.id);
      let newLetterheadUrl = state.letterheadUrl;

      if (state.letterheadFile) {
        newLetterheadUrl = await uploadLetterhead(state.letterheadFile, company.id);
      }

      await updateDoc(companyRef, {
  shortCode: state.shortCode || "",
  letterheadUrl: newLetterheadUrl || "",
});

// 🔹 update local preview immediately
setFormState((prev) => ({
  ...prev,
  [company.id]: {
    ...prev[company.id],
    letterheadUrl: newLetterheadUrl,
    letterheadFile: null,
  },
}));

alert("✅ Letterhead updated successfully!");

    } catch (err) {
      console.error("❌ Error updating letterhead:", err);
      alert("❌ Failed to update letterhead: " + err.message);
    } finally {
      setSavingCompanyId(null);
    }
  };

  // 🔹 Handle text or file changes
  const handleFieldChange = (companyId, field, value) => {
    setFormState((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        [field]: value,
      },
    }));
  };

  const handleFileChange = (companyId, file) => {
    setFormState((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        letterheadFile: file,
      },
    }));
  };

  // 🔹 UI Styles
  const cardStyle = {
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
    padding: "20px",
    marginBottom: "24px",
    maxWidth: "1000px",
    margin: "0 auto",
    background: "#fff",
  };

  const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#444" };
  const textInputStyle = {
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "8px",
    fontSize: "14px",
    width: "100%",
  };
  const fileInputStyle = {
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "8px",
    background: "#fafafa",
    fontSize: "13px",
    width: "100%",
  };
  const previewBox = {
    border: "1px solid #ddd",
    borderRadius: "8px",
    background: "#fff",
    padding: "10px",
    textAlign: "center",
    minHeight: "120px",
    fontSize: "12px",
  };

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ margin: "0 0 20px", fontWeight: 600, color: "#222" }}>
        🏢 Company Letterheads
      </h2>
      <p style={{ marginTop: 0, color: "#555", fontSize: "14px" }}>
        Upload or update official company letterheads. These are used as
        document backgrounds for all generated letters.
      </p>

      {companies.length === 0 ? (
        <p style={{ color: "#777" }}>No companies found.</p>
      ) : (
        companies.map((company) => {
          const state = formState[company.id] || {};
          return (
            <Card key={company.id} style={cardStyle}>
              <CardContent style={{ padding: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "16px" }}>
                    {company.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    ID: {company.id}
                  </div>
                </div>

                {/* Short Code Input */}
                <div style={{ marginBottom: "16px" }}>
                  <div style={labelStyle}>Reference Short Code</div>
                  <input
                    style={textInputStyle}
                    placeholder="e.g. CLL, LLL"
                    value={state.shortCode || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        company.id,
                        "shortCode",
                        e.target.value.toUpperCase()
                      )
                    }
                  />
                  <div style={{ fontSize: "12px", color: "#777", marginTop: "4px" }}>
                    Used in auto reference numbers (e.g.{" "}
                    <b>{(state.shortCode || "CLL")}/WL/2025/0001</b>)
                  </div>
                </div>

                {/* Letterhead Upload */}
                <div style={{ marginBottom: "16px" }}>
                  <div style={labelStyle}>Letterhead (JPG/PNG)</div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    style={fileInputStyle}
                    onChange={(e) =>
                      handleFileChange(
                        company.id,
                        e.target.files && e.target.files[0]
                      )
                    }
                  />
                  <div
                    style={{ fontSize: "12px", color: "#777", marginTop: "4px" }}
                  >
                    Recommended A4 size JPG/PNG for professional quality.
                  </div>

                  <div style={{ marginTop: "8px", ...previewBox }}>
                    <div style={{ fontWeight: 600, marginBottom: "6px" }}>
                      Current Letterhead
                    </div>
                    {state.letterheadUrl ? (
                      <img
                        src={state.letterheadUrl}
                        alt="Letterhead"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "90px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div style={{ color: "#999" }}>No letterhead uploaded</div>
                    )}
                  </div>
                </div>

                {/* Save button */}
                <div style={{ textAlign: "right", marginTop: "20px" }}>
                  <button
                    disabled={savingCompanyId === company.id}
                    onClick={() => handleSaveCompany(company)}
                    style={{
                      background: "#1976d2",
                      color: "#fff",
                      padding: "10px 16px",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      minWidth: "140px",
                    }}
                  >
                    {savingCompanyId === company.id ? "Saving..." : "💾 Save"}
                  </button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};
